import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { resumeApp, type AdminRegistryApp } from '@/api/console';
import { Button, SectionCard, useToast } from '@/components/ui';
import { SuspendAppModal } from './SuspendAppModal';
import { RetireAppModal } from './RetireAppModal';

type DangerModal = 'suspend' | 'retire' | null;

export interface AppDetailConsiderationsCardProps {
    app: AdminRegistryApp;
    canManage: boolean;
}

/**
 * EAA-1884: "Việc cần cân nhắc" — the frame 49 danger card the EAA-1817 plan
 * (`docs/plans/PLAN-EAA-1817-app-detail-and-role-map-refinement.md` §2.4/§5
 * gap #1) recorded as omitted because no backing endpoint existed. It does
 * now (`suspendApp`/`resumeApp`/`retireApp`, `api/console.ts`), so this closes
 * that gap for real.
 *
 * Gated on BOTH `canManage` (same `console.apps.manage` capability
 * `FlipAppModal`'s enable/disable action already uses — no new permission
 * code) and `app.lifecycle`: a `pending` app was never running (nothing to
 * suspend) and a `retired` one already is (nothing further to do), so the
 * card renders for `active`/`suspended` only.
 */
export function AppDetailConsiderationsCard({ app, canManage }: AppDetailConsiderationsCardProps) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();
    const [openModal, setOpenModal] = useState<DangerModal>(null);

    const resumeMutation = useMutation({
        mutationFn: () => resumeApp(config.apiBase, accessToken as string, app.code),
        onSuccess: () => {
            toast.success(
                t('console.appDetail.overview.considerations.resumeToastSuccess', { name: app.name })
            );
            void queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
            void queryClient.invalidateQueries({ queryKey: ['apps-registry'] });
            void queryClient.invalidateQueries({ queryKey: ['role-map-sync-status'] });
        },
        onError: (error: unknown) => {
            const message = error instanceof Error ? error.message : undefined;
            toast.error(message || t('console.appDetail.overview.considerations.resumeToastError'));
        },
    });

    if (!canManage) return null;
    if (app.lifecycle !== 'active' && app.lifecycle !== 'suspended') return null;

    return (
        <>
            <SectionCard
                className="app-detail-danger-card"
                // EAA-1884: SectionCard's default ring is an inline style, which a
                // plain CSS class cannot out-specificity — `style` merges after
                // and wins, per SectionCard's own "merge order is the contract"
                // doc. `color-mix` against the existing `--color-error` token
                // matches the design's `rgba(212,24,61,.30)` ring without a new
                // hardcoded literal (`--color-error` IS `#d4183d`) — same
                // technique 08-dashboard.css's own danger ring already uses.
                style={{ boxShadow: '0 0 0 1px color-mix(in srgb, var(--color-error) 30%, transparent)' }}
                title={t('console.appDetail.overview.considerations.title')}
            >
                {app.lifecycle === 'active' && (
                    <div className="app-detail-danger-row">
                        <div className="app-detail-danger-row__body">
                            <strong>
                                {t('console.appDetail.overview.considerations.suspendRowTitle')}
                            </strong>
                            <span>{t('console.appDetail.overview.considerations.suspendRowHint')}</span>
                        </div>
                        <Button onClick={() => setOpenModal('suspend')}>
                            {t('console.appDetail.overview.considerations.suspendButton')}
                        </Button>
                    </div>
                )}

                {app.lifecycle === 'suspended' && (
                    <div className="app-detail-danger-row">
                        <div className="app-detail-danger-row__body">
                            <strong>
                                {t('console.appDetail.overview.considerations.suspendedRowTitle')}
                            </strong>
                            <span>
                                {app.suspended_reason ||
                                    t('console.appDetail.overview.considerations.suspendedNoReason')}
                            </span>
                            {app.suspended_at && (
                                <span className="app-detail-danger-row__meta">
                                    {t('console.appDetail.overview.considerations.suspendedAt', {
                                        time: new Date(app.suspended_at).toLocaleString('vi-VN'),
                                    })}
                                </span>
                            )}
                        </div>
                        <Button
                            type="primary"
                            onClick={() => resumeMutation.mutate()}
                            disabled={resumeMutation.isPending}
                        >
                            {resumeMutation.isPending
                                ? t('console.appDetail.overview.considerations.resumePending')
                                : t('console.appDetail.overview.considerations.resumeButton')}
                        </Button>
                    </div>
                )}

                <div className="app-detail-danger-row">
                    <div className="app-detail-danger-row__body">
                        <strong>{t('console.appDetail.overview.considerations.retireRowTitle')}</strong>
                        <span>{t('console.appDetail.overview.considerations.retireRowHint')}</span>
                    </div>
                    <Button danger onClick={() => setOpenModal('retire')}>
                        {t('console.appDetail.overview.considerations.retireButton')}
                    </Button>
                </div>
            </SectionCard>

            <SuspendAppModal
                app={openModal === 'suspend' ? app : null}
                onClose={() => setOpenModal(null)}
            />
            <RetireAppModal app={openModal === 'retire' ? app : null} onClose={() => setOpenModal(null)} />
        </>
    );
}

export default AppDetailConsiderationsCard;
